/**
 * Defensive `sessionStorage` helpers that no-op (or return `null`) when
 * `sessionStorage` is unavailable or access is blocked — for example, during
 * SSR where `sessionStorage` is undefined, or in browser contexts that deny
 * storage access (Safari private mode, iframe sandboxes, quota exceeded,
 * SecurityError from cross-origin restrictions).
 *
 * Used by the Google reauth flow to coordinate state across the OAuth
 * round-trip, and by client-side persistence (e.g. undo state) that must
 * survive a reload without crashing in any of the above contexts.
 */

/** Safe sessionStorage read — returns `null` if unavailable. */
export function safeSessionRead(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Safe sessionStorage remove — no-op if unavailable. Accepts one or more keys. */
export function safeSessionRemove(...keys: string[]): void {
  try {
    for (const key of keys) sessionStorage.removeItem(key);
  } catch {
    // sessionStorage may not be available
  }
}

/** Safe sessionStorage write — no-op if unavailable. */
export function safeSessionWrite(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage may not be available
  }
}
